import { type ReactElement, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, Tooltip } from '@mui/material'

import { apiZodios } from '~api/apiZodios'
import { Chip, Dropdown, type DropdownOption, Icon, Typography } from '~components/internal'
import { type RootState } from '~redux/rootReducer'
import { typography } from '~tokens/internal'
import { type GISLayer, type PageSourceSummary } from '~types'
import { getBasePath } from '~utils/navigationUtils'

import { useAISettings } from '../../../AISettingsScreen/AISettingsContext'
import { type ExpectedSource, expectedSourceKey, expectedSourceLabel } from '../../expectedSources'

import './SourceSearchPicker.scss'

const SEARCH_DEBOUNCE_MS = 200
const SEARCH_LIMIT = 20

interface SourceSearchPickerProps {
  value: ExpectedSource[]
  onChange: (sources: ExpectedSource[]) => void
}

function SourceSearchPicker({ value, onChange }: SourceSearchPickerProps): ReactElement {
  const user = useSelector((state: RootState) => state?.auth.user)
  const { selectedOrganization } = useAISettings()
  const [inputValue, setInputValue] = useState('')
  const [pages, setPages] = useState<PageSourceSummary[]>([])
  const [titles, setTitles] = useState<Record<string, string>>({})
  const [pageSourceIds, setPageSourceIds] = useState<Record<string, string>>({})
  const [gisLayers, setGisLayers] = useState<GISLayer[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestIdRef = useRef(0)
  const titleLookupsRef = useRef(new Set<string>())

  useEffect(() => {
    return (): void => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      requestIdRef.current += 1
    }
  }, [])

  useEffect(() => {
    titleLookupsRef.current.clear()
    setTitles({})
    setPageSourceIds({})
  }, [selectedOrganization?.id])

  useEffect(() => {
    if (!user?.id || !selectedOrganization?.id) return
    value
      .filter((s): s is string => typeof s === 'string' && !titleLookupsRef.current.has(s))
      .forEach((url) => {
        titleLookupsRef.current.add(url)
        apiZodios
          .ai_listPageSources({
            user_id: user.id,
            organization_identity_id: selectedOrganization.id,
            filter_url: url,
            limit: SEARCH_LIMIT,
          })
          .then((res) => {
            const page = res.pages.find((p) => p.url === url)
            if (page) {
              setTitles((prev) => ({ ...prev, [url]: page.title }))
              setPageSourceIds((prev) => ({ ...prev, [url]: page.page_source_id }))
            }
          })
          .catch(() => {})
      })
  }, [value, user?.id, selectedOrganization?.id])

  const fetchSources = (query: string): void => {
    if (!user?.id || !selectedOrganization?.id) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const requestId = ++requestIdRef.current
    debounceRef.current = setTimeout(() => {
      setIsLoading(true)
      const params = { user_id: user.id, organization_identity_id: selectedOrganization.id }
      Promise.all([
        apiZodios
          .ai_listPageSources({
            ...params,
            filter_query: query,
            filter_archived: false,
            limit: SEARCH_LIMIT,
          })
          .then((res) => res.pages)
          .catch(() => []),
        apiZodios
          .ai_listGISLayerSources({
            ...params,
            query,
            limit: SEARCH_LIMIT,
            refresh_geometry_counts: false,
          })
          .then((res) => res.gis_layers)
          .catch(() => []),
      ]).then(([nextPages, nextLayers]) => {
        if (requestId !== requestIdRef.current) return
        setPages(nextPages)
        setGisLayers(nextLayers)
        setIsLoading(false)
      })
    }, SEARCH_DEBOUNCE_MS)
  }

  const selected = new Set(value.map(expectedSourceKey))

  const options: DropdownOption[] = [
    ...gisLayers
      .filter((layer) => !selected.has(layer.source))
      .map((layer) => ({
        value: layer.source,
        label: layer.name,
        group: 'GIS Layers',
        avatar: <Icon name='map' size={typography.fontSizePx.sm} />,
        detailed_information: layer.description ? (
          <Typography maxChars={70} variant='caption' sx={{ opacity: 0.6 }}>
            {layer.description}
          </Typography>
        ) : undefined,
      })),
    ...pages
      .filter((page) => !selected.has(page.url))
      .map((page) => ({
        value: page.url,
        label: page.title || page.url,
        group: 'Pages',
        avatar: <Icon name='language' size={typography.fontSizePx.sm} />,
        detailed_information: page.title ? (
          <Typography maxChars={70} variant='caption' sx={{ opacity: 0.6 }}>
            {page.url}
          </Typography>
        ) : undefined,
      })),
  ]

  const handleSelect = (val: string | string[]): void => {
    const key = Array.isArray(val) ? val[0] : val
    if (!key || selected.has(key)) return
    const layer = gisLayers.find((l) => l.source === key)
    const page = pages.find((p) => p.url === key)
    if (page) {
      titleLookupsRef.current.add(key)
      setTitles((prev) => ({ ...prev, [key]: page.title }))
      setPageSourceIds((prev) => ({ ...prev, [key]: page.page_source_id }))
    }
    onChange([...value, layer ? { source_id: layer.source, name: layer.name } : key])
    setInputValue('')
    setPages([])
    setGisLayers([])
  }

  return (
    <Box className='source-search-picker'>
      {value.length > 0 && (
        <Box className='source-search-picker__chips'>
          {value.map((source, i) => {
            const pageSourceId = typeof source === 'string' ? pageSourceIds[source] : undefined
            const canOpen = Boolean(pageSourceId && selectedOrganization?.handle)
            const handleOpen = (): void => {
              if (!canOpen) return
              window.open(
                `${window.location.origin}${getBasePath()}/organizations/${selectedOrganization?.handle}/sources/${pageSourceId}`,
                '_blank',
              )
            }
            return (
              <Tooltip key={expectedSourceKey(source)} title={expectedSourceLabel(source)} arrow>
                <Chip
                  size='small'
                  icon={
                    <Icon
                      name={typeof source === 'string' ? 'language' : 'map'}
                      size={typography.fontSizePx.xs}
                    />
                  }
                  label={typeof source === 'string' ? (titles[source] ?? source) : source.name}
                  onClick={canOpen ? handleOpen : undefined}
                  className={canOpen ? 'source-search-picker__chip--openable' : undefined}
                  onDelete={() => onChange(value.filter((_, j) => j !== i))}
                />
              </Tooltip>
            )
          })}
        </Box>
      )}
      <Dropdown
        options={options}
        value={undefined}
        onChange={handleSelect}
        searchable
        searchValue={inputValue}
        onSearchChange={(q) => {
          setInputValue(q)
          fetchSources(q)
        }}
        loading={isLoading}
        startAdornment={<Icon name='search' />}
        placeholder='Search pages and GIS layers...'
        fullWidth
      />
    </Box>
  )
}

export default SourceSearchPicker
